/* Cuddypup: hide unavailable Mageworx deals or products by offer behaviour. */
(() => {
  'use strict';

  if (window.__cuddypupMageworxStockVisibility) return;

  const HIDDEN_CLASS = 'cuddypup-mw-stock-hidden';
  const HOST_STYLE_ID = 'cuddypup-mw-stock-visibility-host-style';
  const availabilityCache = new Map();
  const originalPresentation = new WeakMap();
  const observedRoots = new WeakSet();
  const scheduledHosts = new WeakSet();
  const evaluationVersions = new WeakMap();

  const rules = {
    /* These offers only work when every product can be bought together. */
    'MW-BUNDLE-OFFER': {
      mode: 'atomic',
      itemSelector: '.BundleOffer__Item',
    },
    'MW-BOGO-OFFER': {
      mode: 'atomic',
      itemSelector: '.BogoOffer__Item',
    },
    'MW-BUNDLE-PRODUCT': {
      mode: 'atomic',
      itemSelector: '.BundleProduct__Item, .BundleOffer__Item',
    },

    /* These are choice/recommendation surfaces. Remove only unavailable cards. */
    'MW-PRODUCT-ADDONS': {
      mode: 'filter',
      itemSelector: '.ProductAddons__Item',
    },
    'MW-PDP-OFFER': {
      mode: 'filter',
      itemSelector: '.Category-InlineProductCard__ProductCard, [class*="ProductCard__ProductCard"]',
    },
    'MW-BUILD-OFFER': {
      mode: 'filter',
      itemSelector: '.Category-InlineProductCard__ProductCard, [class*="ProductCard__ProductCard"]',
    },
    'MW-OFFERS-DIALOG': {
      mode: 'filter',
      itemSelector: '.ProductSlider > .Slide',
    },

    /* A volume deal has no useful action when its single product is unavailable. */
    'MW-VOLUME-BUNDLE': {
      mode: 'current-product',
      contentSelector: '.VolumeBundle, .VolumeBundle__VolumeBundleGrid, .VolumeBundle__VolumeBundleInline',
    },
    'MW-TIER-OFFER': {
      mode: 'current-product',
      contentSelector: '.TierOffer, .VolumeBundle',
    },
  };

  const productHandle = (value) => {
    if (!value) return null;
    try {
      const url = new URL(value, location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (_) {
      return null;
    }
  };

  const handleForElement = (element) => {
    const link = element.querySelector('a[href*="/products/"]');
    return productHandle(link?.getAttribute('href'));
  };

  const currentProductHandle = () => productHandle(location.pathname);

  const productIsAvailable = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (availabilityCache.has(handle)) return availabilityCache.get(handle);

    const request = fetch(`/products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product lookup failed: ${response.status}`);
        return response.json();
      })
      .then((product) => {
        if (Array.isArray(product?.variants)) {
          return product.variants.some((variant) => variant?.available === true);
        }
        return typeof product?.available === 'boolean' ? product.available : null;
      })
      .catch(() => null);

    availabilityCache.set(handle, request);
    return request;
  };

  const setHidden = (element, hidden) => {
    if (!element) return;

    if (hidden) {
      if (!originalPresentation.has(element)) {
        originalPresentation.set(element, {
          display: element.style.getPropertyValue('display'),
          displayPriority: element.style.getPropertyPriority('display'),
          ariaHidden: element.getAttribute('aria-hidden'),
        });
      }
      element.classList.add(HIDDEN_CLASS);
      element.style.setProperty('display', 'none', 'important');
      element.setAttribute('aria-hidden', 'true');
      return;
    }

    if (!element.classList.contains(HIDDEN_CLASS)) return;
    const original = originalPresentation.get(element);
    element.classList.remove(HIDDEN_CLASS);
    if (original?.display) {
      element.style.setProperty('display', original.display, original.displayPriority || '');
    } else {
      element.style.removeProperty('display');
    }
    if (original?.ariaHidden === null || original?.ariaHidden === undefined) {
      element.removeAttribute('aria-hidden');
    } else {
      element.setAttribute('aria-hidden', original.ariaHidden);
    }
    originalPresentation.delete(element);
  };

  const evaluateAtomic = async (host, root, rule, version) => {
    const items = [...root.querySelectorAll(rule.itemSelector)];
    const handles = items.map(handleForElement).filter(Boolean);
    if (!handles.length) return;

    const states = await Promise.all(handles.map(productIsAvailable));
    if (evaluationVersions.get(host) !== version) return;

    /* A failed lookup is unknown, not sold out. Never hide a deal on a network error. */
    setHidden(host, states.some((available) => available === false));
  };

  const evaluateFilter = async (host, root, rule, version) => {
    const items = [...root.querySelectorAll(rule.itemSelector)];
    if (!items.length) return;

    const records = items.map((item) => ({ item, handle: handleForElement(item) }));
    const states = await Promise.all(
      records.map(({ handle }) => (handle ? productIsAvailable(handle) : Promise.resolve(null)))
    );
    if (evaluationVersions.get(host) !== version) return;

    records.forEach(({ item }, index) => setHidden(item, states[index] === false));

    /* Keep the widget when any product is available or unknown. Hide only when
       every rendered product was positively confirmed unavailable. */
    const allUnavailable = states.length > 0 && states.every((available) => available === false);
    setHidden(host, allUnavailable);
  };

  const evaluateCurrentProduct = async (host, root, rule, version) => {
    if (!root.querySelector(rule.contentSelector)) return;
    const available = await productIsAvailable(currentProductHandle());
    if (evaluationVersions.get(host) !== version) return;
    setHidden(host, available === false);
  };

  const evaluate = async (host) => {
    const rule = rules[host.tagName];
    const root = host.shadowRoot;
    if (!rule || !root) return;

    const version = (evaluationVersions.get(host) || 0) + 1;
    evaluationVersions.set(host, version);

    if (rule.mode === 'atomic') await evaluateAtomic(host, root, rule, version);
    if (rule.mode === 'filter') await evaluateFilter(host, root, rule, version);
    if (rule.mode === 'current-product') {
      await evaluateCurrentProduct(host, root, rule, version);
    }
  };

  const scheduleHost = (host) => {
    if (scheduledHosts.has(host)) return;
    scheduledHosts.add(host);
    requestAnimationFrame(() => {
      scheduledHosts.delete(host);
      evaluate(host);
    });
  };

  const ensureHostStyle = (root) => {
    if (root.getElementById(HOST_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = HOST_STYLE_ID;
    style.textContent = `
      :host(.${HIDDEN_CLASS}) {
        display: none !important;
      }
    `;
    root.append(style);
  };

  const install = (host) => {
    if (!rules[host.tagName]) return;
    const root = host.shadowRoot;
    if (!root) return;

    ensureHostStyle(root);

    if (!observedRoots.has(root)) {
      observedRoots.add(root);
      new MutationObserver(() => {
        ensureHostStyle(root);
        scheduleHost(host);
      }).observe(root, {
        childList: true,
        subtree: true,
      });
    }
    scheduleHost(host);
  };

  const scan = () => {
    if (!window.document?.documentElement) return;
    Object.keys(rules).forEach((tagName) => {
      window.document.querySelectorAll(tagName.toLowerCase()).forEach(install);
    });
  };

  new MutationObserver(scan).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  /* Mageworx attaches some open shadow roots lazily without changing the light DOM. */
  let attempts = 0;
  const rootTimer = window.setInterval(() => {
    scan();
    attempts += 1;
    if (attempts >= 120) window.clearInterval(rootTimer);
  }, 250);

  window.__cuddypupMageworxStockVisibility = {
    rescan: scan,
    clearCache: () => availabilityCache.clear(),
    hiddenClass: HIDDEN_CLASS,
  };

  scan();
})();
